import { useState, useEffect } from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';

const CLOUDFLARE_URL = import.meta.env.VITE_ENV === "dev"
    ? import.meta.env.VITE_DEV_CLOUDFLARE_URL
    : import.meta.env.VITE_ENV === "staging"
    ? import.meta.env.VITE_STAGING_CLOUDFLARE_URL
    : import.meta.env.VITE_CLOUDFLARE_URL;

export const Unsubscribe = () => {
    const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const [message, setMessage] = useState('');
    const [email, setEmail] = useState('');

    useEffect(() => {
        const unsubscribe = async () => {
            try {
                const params = new URLSearchParams(window.location.search);
                const emailParam = params.get('email');
                const token = params.get('token');

                if (!emailParam || !token) {
                    setStatus('error');
                    setMessage('Invalid unsubscribe link. The link may be malformed or incomplete.');
                    return;
                }

                setEmail(emailParam);

                const response = await fetch(`${CLOUDFLARE_URL}/unsubscribe?email=${encodeURIComponent(emailParam)}&token=${token}`, {
                    method: 'GET',
                });

                const data = await response.json();

                if (response.ok) {
                    setStatus('success');
                    setMessage('You have been successfully unsubscribed from our email list.');
                } else {
                    setStatus('error');
                    setMessage(data.error === 'Invalid token'
                        ? 'Invalid or expired unsubscribe link.'
                        : 'An error occurred while processing your request.');
                }
            } catch (error) {
                console.error('Unsubscribe error:', error);
                setStatus('error');
                setMessage('An error occurred while processing your request. Please try again later.');
            }
        };

        unsubscribe();
    }, []);

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center p-4">
            <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
                {status === 'loading' && (
                    <>
                        <div className="mb-6 flex justify-center">
                            <div className="w-5 h-5 border-3 border-orange-500 border-t-transparent rounded-full animate-spin" />
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">Processing...</h1>
                        <p className="text-gray-600">Please wait while we process your request.</p>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <div className="mb-6 flex justify-center">
                            <div className="relative">
                                <div className="absolute inset-0 bg-green-500/20 rounded-full blur-xl animate-pulse" />
                                <CheckCircle2 className="relative size-16 text-green-500" strokeWidth={2} />
                            </div>
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">Successfully Unsubscribed</h1>
                        <p className="text-gray-600 mb-4">{message}</p>
                        {email && (
                            <p className="text-sm text-gray-500 mb-6">
                                Email: <span className="font-mono">{email}</span>
                            </p>
                        )}
                        <p className="text-sm text-gray-500">
                            If this was a mistake, please contact us at{' '}
                            <a href="mailto:hey@bettercampus.com" className="text-blue-600 hover:underline">
                                hey@bettercampus.com
                            </a>
                        </p>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <div className="mb-6 flex justify-center">
                            <div className="relative">
                                <div className="absolute inset-0 bg-red-500/20 rounded-full blur-xl animate-pulse" />
                                <XCircle className="relative size-16 text-red-500" strokeWidth={2} />
                            </div>
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">Unable to Unsubscribe</h1>
                        <p className="text-gray-600 mb-6">{message}</p>
                        <p className="text-sm text-gray-500">
                            If you continue to receive emails, please contact us at{' '}
                            <a href="mailto:hey@bettercampus.com" className="text-blue-600 hover:underline">
                                hey@bettercampus.com
                            </a>
                        </p>
                    </>
                )}

                <div className="mt-8 pt-6 border-t border-gray-200">
                    <img src="/extlogo.png" alt="BetterCampus" className="h-5 mx-auto mb-2 opacity-50" />
                    <p className="text-xs text-gray-400">BetterCampus</p>
                </div>
            </div>
        </div>
    );
};
